import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { CardBody } from '@m/alchemy-ui/CardBody';
import { FieldStatus } from '@m/alchemy-ui/FieldStatus';
import { DragDropIcon } from '@m/alchemy-ui/icons/DragDropIcon';
import { VisuallyHidden } from '@m/alchemy-ui/VisuallyHidden';
import { canDropOnPanel, type PanelConfigPatch } from '../common/panel-grid';
import {
  AUTO_PANEL_SIZE,
  type PageCmsPanel,
  type PanelSize,
  panelTypesByTypename,
  WIDE_PANEL_SIZE
} from '../common/panel-types';
import { getPanelCardError, getPanelConfigError } from '../common/panel-validation';
import {
  PanelCard,
  PanelCardHeader,
  PanelDragHandle,
  PanelErrorStatus,
  PanelHeaderTitle,
  PanelItemWrapper
} from '../CustomPage.styled';
import { PanelContent } from './PanelContent';
import { PanelOptionsMenu } from './PanelOptionsMenu';

const DROP_PREVIEW_ID_PREFIX = 'drop-preview-';

const messages = defineMessages({
  position: {
    id: 'editCustomPagePanelRowPosition',
    defaultMessage: 'Row {row}, position {position} of {total}'
  }
});

interface PanelItemProps {
  readonly panel: PageCmsPanel;
  readonly panelCount: number;
  readonly rowIndex: number;
  readonly panelIndex: number;
  readonly showPanelErrors: boolean;
  /** Preview copies skip DnD registration and get no callbacks, so they are visual only. */
  readonly isDropPreview?: boolean;
  readonly isDropPreviewIncoming?: boolean;
  readonly onSizeChange?: (rowIndex: number, panelId: string, size: PanelSize) => void;
  readonly onDeletePanel?: (rowIndex: number, panelId: string) => void;
  readonly onMovePanel?: (panelId: string, trigger: HTMLElement) => void;
  readonly onConfigChange?: (rowIndex: number, panelId: string, patch: PanelConfigPatch) => void;
}

const PanelItemComponent = ({
  panel,
  panelCount,
  rowIndex,
  panelIndex,
  showPanelErrors,
  isDropPreview = false,
  isDropPreviewIncoming = false,
  onSizeChange,
  onDeletePanel,
  onMovePanel,
  onConfigChange
}: PanelItemProps) => {
  const intl = useIntl();
  const [isDragging, setIsDragging] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLSpanElement>(null);
  const panelId = panel.id;
  const panelType = panelTypesByTypename[panel.settings.__typename];
  const panelName = intl.formatMessage(panelType.message);
  const idPrefix = isDropPreview ? DROP_PREVIEW_ID_PREFIX : '';
  const titleId = `${idPrefix}panel-title-${panelId}`;
  const positionId = `${idPrefix}panel-position-${panelId}`;
  const errorId = `${idPrefix}panel-error-${panelId}`;

  // "wide" only applies in 2-panel rows — same rule as the public renderer (PageItem.tsx).
  const isWide = panelCount === 2 && panel.settings.size === WIDE_PANEL_SIZE;
  const size = isWide ? WIDE_PANEL_SIZE : AUTO_PANEL_SIZE;

  // Derived from the config (not stored) so errors clear as soon as the panel is fixed; only
  // surfaced after a save attempt, matching the page-title validation. Video and link-config
  // error messages are rendered inside their editors, so the card shows only the border for them.
  const hasError = showPanelErrors && getPanelConfigError(panel.settings) !== null;
  const cardError = showPanelErrors ? getPanelCardError(panel.settings) : null;

  const handleSizeChange = useCallback(
    (value: PanelSize) => onSizeChange?.(rowIndex, panelId, value),
    [onSizeChange, rowIndex, panelId]
  );

  const handleDelete = useCallback(() => onDeletePanel?.(rowIndex, panelId), [onDeletePanel, rowIndex, panelId]);

  const handleMove = useCallback((trigger: HTMLElement) => onMovePanel?.(panelId, trigger), [onMovePanel, panelId]);

  const handleConfigChange = useCallback(
    (patch: PanelConfigPatch) => onConfigChange?.(rowIndex, panelId, patch),
    [onConfigChange, rowIndex, panelId]
  );

  // The deps only change on grid commits (drop or delete), never mid-drag, so registrations survive a drag.
  useEffect(() => {
    const element = wrapperRef.current;
    const dragHandle = dragHandleRef.current;

    if (!element || !dragHandle || isDropPreview) {
      return undefined;
    }

    return combine(
      draggable({
        element,
        dragHandle,
        getInitialData: () => ({ type: 'panel', panelId, rowIndex, panelIndex, isOnlyPanelInRow: panelCount === 1 }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false)
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => canDropOnPanel(source.data, panelId, rowIndex, panelCount),
        getIsSticky: () => true,
        getData: ({ input, element: targetElement }) =>
          attachClosestEdge(
            { type: 'panel', rowIndex, panelIndex },
            { input, element: targetElement, allowedEdges: ['left', 'right'] }
          )
      })
    );
  }, [panelId, rowIndex, panelIndex, panelCount, isDropPreview]);

  return (
    <PanelItemWrapper
      ref={wrapperRef}
      role="group"
      aria-labelledby={`${titleId} ${positionId}`}
      aria-describedby={cardError ? errorId : undefined}
      $isWide={isWide}
      $isDragging={isDragging}
      data-testid={`${isDropPreview ? 'panel-drop-preview' : 'panel-item'}-${panelId}`}
      data-panel-type={panelType.id}
      data-incoming={isDropPreviewIncoming}
      data-size={size}
    >
      <VisuallyHidden id={positionId}>
        {intl.formatMessage(messages.position, {
          row: rowIndex + 1,
          position: panelIndex + 1,
          total: panelCount
        })}
      </VisuallyHidden>
      <PanelCard $hasError={hasError} $isDropPreviewIncoming={isDropPreviewIncoming} hasBorder shadowDepth="none">
        <PanelCardHeader>
          <PanelHeaderTitle ref={dragHandleRef}>
            <PanelDragHandle aria-hidden="true" data-testid="panel-drag-handle">
              <DragDropIcon size={20} aria-hidden="true" />
            </PanelDragHandle>
            <span id={titleId} data-testid="panel-title">
              {panelName}
            </span>
          </PanelHeaderTitle>
          <PanelOptionsMenu
            panelId={panelId}
            panelName={panelName}
            rowIndex={rowIndex}
            panelIndex={panelIndex}
            panelCount={panelCount}
            showSizeSelect={panelCount === 2}
            size={size}
            labelId={`${idPrefix}panel-size-label-${panelId}`}
            onSizeChange={onSizeChange && handleSizeChange}
            onDelete={onDeletePanel && handleDelete}
            onMove={onMovePanel && handleMove}
          />
        </PanelCardHeader>
        <CardBody>
          {cardError && (
            <PanelErrorStatus>
              <FieldStatus
                id={errorId}
                level="error"
                message={intl.formatMessage(cardError)}
                aria-live="off"
                data-testid="panel-card-error"
              />
            </PanelErrorStatus>
          )}
          <PanelContent
            panel={panel}
            showPanelErrors={showPanelErrors}
            onConfigChange={handleConfigChange}
            idPrefix={idPrefix}
          />
        </CardBody>
      </PanelCard>
    </PanelItemWrapper>
  );
};

export const PanelItem = memo(PanelItemComponent);
